import { Component, Output } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { PTicketService } from '../../../services/p-ticket.service';
import { pTicket } from '../../../interfaces/pTicket';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { distinctUntilChanged, filter, map, Observable } from 'rxjs';
import { MatSelectModule } from '@angular/material/select';
import { AsyncPipe, NgIf, NgForOf } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { LoginService } from '../../../services/login.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { SessionWarningDialogComponent } from '../session-warning-dialog/session-warning-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { EventEmitter } from '@angular/core';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { environment } from '../../../../environments/environment';
import { MatTooltip } from '@angular/material/tooltip';
import { globalUser } from '../../../helpers/globalFields';
import { SocketService } from '../../../services/socket.service';
import { OrganizationService } from '../../../services/organization.service';
import { Organization } from '../../../interfaces/organization';
import { PermissionSocketService } from '../../../services/permission-socket.service';

/**
 * Simple navigation bar
 */
@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule,
    MatSlideToggleModule,
    MatSelectModule,
    NgIf,
    AsyncPipe,
    MatProgressBarModule,
    MatMenu,
    MatMenuModule,
    RouterModule,
    MatTooltip,
    NgForOf,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent {
  /**
   * cosntructor
   *
   * @param router angular routing
   * @param pTicketService used for display of badge with number of open tickets
   * @param loginService used to check if a user is logged in and change the navbar html view accodingly
   */
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private pTicketService: PTicketService,
    private loginService: LoginService,
    public dialog: MatDialog,
    private socketService: SocketService,
    private permissionSocketService: PermissionSocketService,
    public organizationService: OrganizationService,
  ) {;
  }
  currentApplicationVersion = environment.appVersion;
  currentApplicationTitle = environment.appTitle;
  currentApplicationLogo = environment.appFavicon;

  /**
   * boolean to change the html view of the navbar depending on if a user is logged in or not
   */
  isLoggedIn = false;
  /**
   * list of pTickets. Only needs the open ones
   * TODO: Refactor so that it makes use of the newly implemented httpParams for ticket filtering
   */
  pTickets!: pTicket[];
  /**
   * badge value that displays the amount of open tickets currently in the database
   */
  badgeVal = '';

  protected globalUser = globalUser;

  isSystemAdmin: boolean = false;
  isOrganizationAdmin: boolean = true;
  organizationList: Organization[] = [];
  currentOrganization!: string;
  timeLeft$!: Observable<number>;
  sessionPercent$!: Observable<number>;
  @Output() menuClick = new EventEmitter<string>();
  @Output() isLoggedInEvent = new EventEmitter<boolean>();
  /**
   * navigates to ticket overview component
   */
  onpTicketsListClick(): void {
    // Update the URL to merge current query params (filters stay)
    this.router.navigate(['pTickets/list'], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
    });
  }

  /**
   * navigates to login component
   */
  onLoginClick(): void {
    this.router.navigate(['/login']);
  }

  onOrganizationChangeClick(organization: Organization): void {
    this.loginService.refresh(organization.org_name).subscribe((response) => {
      globalUser.setToken(
        response.data.accessToken,
        response.data.refreshToken,
      );
      // Update the service
      this.organizationService.setOrganization(organization.org_name);
      this.socketService.reconnect();

      // Update the URL to merge current query params (filters stay)
      //this.router.navigate(['pTickets/list'], {
      //  relativeTo: this.route,
      //  queryParamsHandling: 'merge',
      //});
    });
  }

  /**
   * upon initilisation checks if the user is logged in or not and then updates the isLoggedIn value, that is used to update the visuals of the navbar.
   */
  ngOnInit() {

    globalUser.getUserStream().subscribe((user) => {
      if (user) {
        this.isSystemAdmin = globalUser.validatePermission('system.admin');
        this.isOrganizationAdmin = globalUser.validatePermission('organization.admin');
      }
    });

    this.timeLeft$ = globalUser.getRemainingSessionTime();
    globalUser.sessionWarning.subscribe((warning) => {
      const dialogRef = this.dialog.open(SessionWarningDialogComponent, {
        width: '100%',
        maxWidth: '800px',
        panelClass: 'custom-dialog-container',
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (!result) {
          globalUser.dismissSessionWarning();
        }
      });
    });
    // update sessionPercent for the progress bar
    this.sessionPercent$ = this.timeLeft$.pipe(
      map((seconds) => {
        const token = globalUser.getAccessToken();
        if (!token) return 0;

        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const totalDuration = payload.exp - payload.iat; // total token lifetime
          return (seconds / totalDuration) * 100;
        } catch {
          return 0;
        }
      }),
    );

    globalUser.getAuthStatus()
    .subscribe((status) => {
      this.isLoggedIn = status;
      this.isLoggedInEvent.emit(this.isLoggedIn);
      if (this.isLoggedIn === true) {
        this.socketService.connect();
        this.permissionSocketService.connect()
        
        this.organizationService
          .getOrganizationListByUser()
          .subscribe((response) => {
            this.organizationList = response;
            this.currentOrganization = globalUser.getUser().org_name || 'none'
          });
      }
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isLoggedIn === true) {
          
          this.organizationService
            .getOrganizationListByUser()
            .subscribe((response) => {
              this.organizationList = response;
            });
        }
      });

    this.socketService.onTicketOpened().subscribe((newValue) => {
      this.badgeVal = newValue.toString();
    });

    this.socketService.onValueChanged().subscribe((newValue) => {
      this.badgeVal = newValue.toString();
    });
  }

  getFormattedTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }


  /**
   * logs the user out
   */
  onLogoutClick() {
    this.loginService.logout();
    globalUser.logout();
    this.permissionSocketService.disconnect();
    this.socketService.disconnect();
    this.router.navigate(['/login']);
  }

  /**
   * routes to reporting map
   */
  onOpenReporting(): void {
    this.router.navigate(['/reporting/map']);
  }

  onProfileClick() {
    this.router.navigate(['/profile/overview']);
  }

  onOpenReportingOverview(): void {
    this.router.navigate(['/reporting/overview']);
  }

  onOpenAdministrationPanel(): void {
    this.router.navigate(['/administration/superadmin/panel'])
  }
}
